import React from 'react';
import { t } from '@citrite/translate';
import {
	AppWindowLinkIcon,
	layout,
	OverflowNavigation,
	palette,
} from '@citrite/web-ui-component';
import { PinnedLink } from '@citrite/workspace-ui-platform';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { trackAnalyticsEvent } from 'analytics';
import {
	StyledDiv,
	StyledImage,
	StyledLightText,
	StyledTile,
	StyledTileText,
	StyledTitleText,
} from 'App/CustomLinkBanner/CustomLink.styled';
import { CustomLinkItem } from 'App/CustomLinkBanner/CustomLinkItem';
import { GenericErrorBoundary } from 'App/GenericErrorBoundary';
import { ConditionalRenderer } from 'Components/ConditionalRenderer';
import { useIsDetailedDesktopTileEnabled } from 'Configuration/hooks';
import { centerContentWidthPX } from 'Workspace/dimensions';
import { PinnedLinksUserEventPayload } from 'Workspace/TelemetryEvents/pinnedLinks/createPinnedLinksCASReporter';

export interface CustomLinkBannerProps {
	customLinkData: PinnedLink[];
	isSmallFormFactor: boolean;
}
const listStyle = css`
	display: flex;
	flex-direction: column;
	gap: ${layout.mediumSpace};
	background-color: ${palette.white};
`;
const menuStyle = css`
	max-height: 25vh;
	padding: ${layout.mediumLargeSpace};
	overflow-y: auto;
	background-color: ${palette.white};
`;

const listTextStyle = css`
	padding: 0;
	background-color: ${palette.white};
`;

const navigationStyleLegacy = css`
	background-color: ${palette.grey200};
`;

const navigationStyleDetailed = css`
	padding: ${layout.space12} ${layout.space20};
	box-shadow: none;
	gap: ${layout.mediumSpace};
	max-width: ${centerContentWidthPX}px;
	margin: 0 auto;
`;

const listItemStyle = css`
	background-color: ${palette.white};
`;

const StyledCenterContentWrapper = styled.div`
	background-color: ${palette.grey200};
`;

const _CustomLinkBanner: React.FC<CustomLinkBannerProps> = (
	props: CustomLinkBannerProps
) => {
	const isDetailedDesktopTileEnabled = useIsDetailedDesktopTileEnabled();
	const navigationStyle = isDetailedDesktopTileEnabled
		? navigationStyleDetailed
		: navigationStyleLegacy;
	return !props.isSmallFormFactor ? (
		<ConditionalRenderer
			flag={isDetailedDesktopTileEnabled}
			OnComponent={StyledCenterContentWrapper}
		>
			<OverflowNavigation
				items={props.customLinkData}
				template={CustomLinkItem}
				stylesList={{
					listStyle,
					menuStyle,
					listTextStyle,
					navigationStyle,
					listItemStyle,
				}}
			/>
		</ConditionalRenderer>
	) : (
		<StyledDiv data-testid="custom-links">
			{props.customLinkData.map((customLink, index) => (
				<StyledTile key={index}>
					{!customLink?.iconUrl ? (
						<AppWindowLinkIcon size={24} />
					) : (
						<StyledImage src={customLink.iconUrl} height={24} width={24} alt="Icon" />
					)}
					<StyledTileText
						href={customLink.hyperlink}
						target="_blank"
						rel="noopener noreferrer"
						onClick={() => {
							trackAnalyticsEvent(PinnedLinksUserEventPayload.pinnedLinkClicked());
						}}
					>
						<StyledTitleText>{customLink.displayText}</StyledTitleText>
						<StyledLightText>{t('Workspace:pinned_links.external_link')}</StyledLightText>
					</StyledTileText>
				</StyledTile>
			))}
		</StyledDiv>
	);
};

export const CustomLinkBanner: React.FC<CustomLinkBannerProps> = (
	props: CustomLinkBannerProps
) => {
	return (
		<GenericErrorBoundary
			renderProp={() => (
				<_CustomLinkBanner
					customLinkData={props.customLinkData}
					isSmallFormFactor={props.isSmallFormFactor}
				/>
			)}
			name="PinnedLinkError"
		/>
	);
};
